import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { API_BASE_URL } from "../utils/constants";
import { Pagination } from "react-bootstrap";
import LoadingSpinner from "../components/LoadingSpinner";
import { getMinDate } from "../utils/hearingDateUtils";
import "./AssignSecondHearingDate.css";

const AssignSecondHearingDate = () => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);
  const [lookupDate, setLookupDate] = useState(null); // stores the 1st hearing date used for lookup
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [secondDate, setSecondDate] = useState(false);
  const [dateError, setDateError] = useState("");

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setFormattedDate(date ? date.toLocaleDateString("en-US") : "");
    setDateError("");
  };

  const handleAppointments = async () => {
    if (!selectedDate) {
      setDateError("Please select the first hearing date.");
      return;
    }
    const dataAppointments = { Hearing_date: formattedDate };
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/GetOppforSecondHearing`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataAppointments),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch data: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      const parsedData = Array.isArray(result) ? result : JSON.parse(result);
      const updatedData = parsedData.map((item) => {
        const { Cust_name, Acc_id, Case_id, Reference_No, ...rest } = item;
        return { Case_id, Reference_No, Cust_name, ...rest };
      });

      setData(updatedData);
      setTotalPages(Math.ceil(updatedData.length / itemsPerPage));
      setLookupDate(selectedDate); // save 1st hearing date as the baseline
      setSelectedDate(null);       // reset so user picks a fresh 2nd hearing date
      setFormattedDate(null);
      setSecondDate(true);
    } catch (error) {
      console.error("Error fetching data:", error);
      alert("Error fetching data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSecondHearing = async () => {
    if (!selectedDate) {
      setDateError("Please select the second hearing date.");
      return;
    }
    if (selectedRows.length === 0) {
      alert("Please select at least one case.");
      return;
    }

    const formattedSecondDate = selectedDate.toLocaleDateString("en-US");
    const payload = selectedRows.map((row) => ({
      Case_id: row.Case_id,
      Second_hearing_date: formattedSecondDate,
    }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/AssignSecond_date`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to set second hearing date: ${response.status} - ${errorText}`);
      }

      alert("Second hearing dates successfully assigned");
      window.location.reload();
    } catch (error) {
      console.error("Error setting second hearing date:", error);
      alert("Error setting second hearing date. Please try again.");
    }
  };

  const headers = data.length > 0 ? Object.keys(data[0]) : [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedRows(!selectAll ? data : []);
  };

  const handleRowSelection = (row) => {
    const isSelected = selectedRows.includes(row);
    setSelectedRows((prev) =>
      isSelected ? prev.filter((r) => r !== row) : [...prev, row]
    );
  };

  useEffect(() => {
    setTotalPages(Math.ceil(data.length / itemsPerPage));
  }, [data, itemsPerPage]);

  const generatePaginationItems = () => {
    const pageItems = [];
    const maxPaginationButtons = 5;
    let startPage = Math.max(currentPage - Math.floor(maxPaginationButtons / 2), 1);
    let endPage = Math.min(startPage + maxPaginationButtons - 1, totalPages);
    if (endPage - startPage + 1 < maxPaginationButtons) {
      startPage = Math.max(endPage - maxPaginationButtons + 1, 1);
    }
    if (startPage > 1) {
      pageItems.push(<Pagination.Item key="start" onClick={() => setCurrentPage(1)}>1</Pagination.Item>);
      if (startPage > 2) pageItems.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    }
    for (let i = startPage; i <= endPage; i++) {
      pageItems.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
          {i}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pageItems.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
      pageItems.push(<Pagination.Item key="end" onClick={() => setCurrentPage(totalPages)}>{totalPages}</Pagination.Item>);
    }
    return pageItems;
  };

  if (loading) return <LoadingSpinner />;

  const minSecondDate = getMinDate(lookupDate);

  return (
    <div className="container">
      <div className="row my-2">
        <div className="col-md-5">
          <h4>Assign Second Hearing Date</h4>
        </div>
      </div>

      <div className="row d-flex align-items-center">
        {!secondDate && (
          <>
            <div className="col-md-3">
              <label htmlFor="datePicker">Select First Hearing Date</label>
            </div>
            <div className="col-md-3">
              <DatePicker
                id="datePicker"
                selected={selectedDate}
                onChange={handleDateChange}
                placeholderText="Select a date"
                dateFormat="MM/dd/yyyy"
                className={`form-control date-picker-sm ${dateError ? "is-invalid" : ""}`}
              />
              {dateError && (
                <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "3px" }}>
                  {dateError}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <button className="custBtn" onClick={handleAppointments}>
                Get Data
              </button>
            </div>
          </>
        )}

        {secondDate && (
          <>
            <div className="col-md-3">
              <label htmlFor="datePicker2">
                Select Second Hearing Date
                <span style={{ fontSize: "0.75rem", color: "#888", marginLeft: "6px" }}>
                  (must be after {lookupDate?.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })})
                </span>
              </label>
            </div>
            <div className="col-md-3">
              <DatePicker
                id="datePicker2"
                selected={selectedDate}
                onChange={handleDateChange}
                placeholderText="Select a date"
                dateFormat="MM/dd/yyyy"
                className={`form-control date-picker-sm ${dateError ? "is-invalid" : ""}`}
                minDate={minSecondDate}
              />
              {dateError && (
                <div style={{ color: "#dc3545", fontSize: "0.8rem", marginTop: "3px" }}>
                  {dateError}
                </div>
              )}
            </div>
            <div className="col-md-3">
              <button className="custBtn" onClick={handleSecondHearing}>
                Set Date
              </button>
            </div>
          </>
        )}
      </div>

      <div className="row">
        <div className="col-md-12">
          <div className="table-responsive">
            {data.length > 0 && (
              <table className="table table-striped table-bordered table-hover mt-3 text-center no-wrap-table">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                    </th>
                    {headers.map((header) => <th key={header}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((item, index) => (
                    <tr key={index} className="text-center custom_fz">
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(item)}
                          onChange={() => handleRowSelection(item)}
                        />
                      </td>
                      {headers.map((header) => <td key={header}>{item[header]}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {totalPages > 1 && (
            <Pagination className="justify-content-center custom-pagination">
              <Pagination.Prev
                onClick={() => setCurrentPage((prev) => (prev > 1 ? prev - 1 : prev))}
                disabled={currentPage === 1}
              />
              {generatePaginationItems()}
              <Pagination.Next
                onClick={() => setCurrentPage((prev) => (prev < totalPages ? prev + 1 : prev))}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignSecondHearingDate;
